'use client';

import { ToastProps } from '@/components/Toast';
import { ToastStatus } from '@/enums/ToastStatus';
import React, { createContext, useCallback, useMemo, useState } from 'react';

const DEFAULT_TIMER = 5000;

interface ToastContextState {
	toasts: ToastProps[];
	showToast: (message: string, status: ToastStatus, timer?: number) => void;
	hideToast: (toastId: string, timer: number) => void;
}

export const ToastContext = createContext<ToastContextState | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = useState<ToastProps[]>([]);

	const showToast = useCallback((message: string, status: ToastStatus, timer: number = DEFAULT_TIMER) => {
		const randomId = crypto.randomUUID();
		setToasts((prevToasts) => [...prevToasts, { message, status, id: randomId }]);
		hideToast(randomId, timer);
	}, []);

	const hideToast = (toastId: string, timer: number) => {
		const timeout = setTimeout(() => {
			setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== toastId));
		}, timer);

		return () => clearTimeout(timeout);
	};

	const contextState = useMemo(() => {
		return { showToast, hideToast, toasts };
	}, [showToast, toasts]);

	return <ToastContext.Provider value={contextState}>{children}</ToastContext.Provider>;
}
